import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock, MoreHorizontal, Pencil, Plus, Shield, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  getCargosControllerListarQueryKey,
  useCargosControllerExcluir,
  useCargosControllerListar,
} from '@/api/generated/admin-cargos/admin-cargos';
import type { CargoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { CargoFormDialogo } from '@/features/admin/cargos/components/CargoFormDialogo';
import { PermissoesDialogo } from '@/features/admin/cargos/components/PermissoesDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

/**
 * Cargos e permissões (F6-2).
 *
 * Sem esta tela, atribuir uma permissão exige SQL — é ela que torna o RBAC
 * utilizável.
 */
export function CargosPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<CargoResponse | undefined>();
  const [permissoesDe, setPermissoesDe] = useState<CargoResponse | null>(null);
  const [aExcluir, setAExcluir] = useState<CargoResponse | null>(null);

  const { data, isPending, isError, refetch } = useCargosControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
  });

  const excluir = useCargosControllerExcluir();

  const podeCriar = pode('admin_cargos.criar');
  const podeEditar = pode('admin_cargos.editar');
  const podeExcluir = pode('admin_cargos.excluir');

  async function confirmarExclusao() {
    if (!aExcluir) return;
    try {
      await excluir.mutateAsync({ id: aExcluir.id });
      await queryClient.invalidateQueries({ queryKey: getCargosControllerListarQueryKey() });
      toast.success(`Cargo "${aExcluir.nome}" excluído.`);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir o cargo.');
    } finally {
      setAExcluir(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Input
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPagina(1);
          }}
          placeholder="Buscar por nome..."
          className="sm:max-w-xs"
        />

        {podeCriar && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Novo cargo
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Cargos</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Shield}
            titulo="Nenhum cargo encontrado"
            descricao={busca ? 'Tente outro termo de busca.' : 'Crie o primeiro cargo.'}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Permissões</TableHead>
                  <TableHead>Usuários</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((cargo) => (
                  <TableRow key={cargo.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{cargo.nome}</span>
                        {cargo.sistema && (
                          <Badge variant="outline" className="gap-1 text-xs">
                            <Lock className="w-3 h-3" />
                            sistema
                          </Badge>
                        )}
                      </div>
                      {cargo.descricao && (
                        <p className="text-xs text-muted-foreground mt-0.5">{cargo.descricao}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {cargo.permissoes.length}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{cargo.totalMembros}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={`Ações de ${cargo.nome}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {podeEditar && (
                            <>
                              <DropdownMenuItem onClick={() => setPermissoesDe(cargo)}>
                                <KeyRound className="w-4 h-4" />
                                Permissões
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setEmEdicao(cargo);
                                  setFormAberto(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                                Renomear
                              </DropdownMenuItem>
                            </>
                          )}

                          {/* Cargo de sistema ou com membros não oferece excluir:
                              o servidor recusa de qualquer forma (409), e um
                              botão que sempre falha é ruído. */}
                          {podeExcluir && !cargo.sistema && cargo.totalMembros === 0 && (
                            <DropdownMenuItem
                              onClick={() => setAExcluir(cargo)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                              Excluir
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <CargoFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { cargo: emEdicao } : {})}
      />

      {permissoesDe && (
        <PermissoesDialogo
          aberto
          aoFechar={() => setPermissoesDe(null)}
          cargo={permissoesDe}
        />
      )}

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir o cargo "{aExcluir?.nome}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O cargo não está atribuído a nenhum usuário.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarExclusao()}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
